/**
 * Push notification registration and listener setup for Capacitor.
 *
 * Call `registerPush(navigate)` once after the user logs in — requests OS
 * permission if not yet granted, then registers for a token.
 * Call `unregisterPush()` on logout to deregister the token and stop listening.
 *
 * On web (no Capacitor) all calls are no-ops — safe to import everywhere.
 */

import type { NavigateFunction } from 'react-router-dom'
import api from './api'
import { handleDeepLink } from './deepLink'

let _listeners: Array<{ remove: () => Promise<void> }> = []
let _currentToken: string | null = null
let _listenersAttached = false

async function _attachListeners(navigate: NavigateFunction): Promise<void> {
  if (_listenersAttached) return
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications')

    const regHandle = await PushNotifications.addListener('registration', async (token) => {
      _currentToken = token.value
      const platform = /iPad|iPhone|iPod/.test(navigator.userAgent) ? 'ios' : 'android'
      try {
        await api.post('/devices/register', { platform, token: token.value })
      } catch (err) {
        console.warn('[Push] token registration failed', err)
      }
    })

    const errHandle = await PushNotifications.addListener('registrationError', (err) => {
      console.warn('[Push] registration error', err)
    })

    const fgHandle = await PushNotifications.addListener(
      'pushNotificationReceived',
      (notification) => {
        window.dispatchEvent(new CustomEvent('push:foreground', { detail: notification }))
      }
    )

    const tapHandle = await PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (action) => {
        const data = action.notification.data as Record<string, string> | undefined
        handleDeepLink(data?.type, data?.lead_id, navigate)
      }
    )

    _listeners = [regHandle, errHandle, fgHandle, tapHandle]
    _listenersAttached = true
  } catch {
    // Running in web / Capacitor unavailable — silently skip
  }
}

/**
 * Register for push notifications immediately after login.
 * Requests OS permission if not yet granted, then calls register() to get a token.
 * Safe to call on web — no-ops if Capacitor is unavailable or permission is denied.
 */
export async function registerPush(navigate: NavigateFunction): Promise<void> {
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications')
    await _attachListeners(navigate)

    const current = await PushNotifications.checkPermissions()
    if (current.receive === 'denied') return

    if (current.receive !== 'granted') {
      const result = await PushNotifications.requestPermissions()
      if (result.receive !== 'granted') return
    }

    await PushNotifications.register()
  } catch {
    // Running in web / Capacitor unavailable — silently skip
  }
}

/**
 * Deregister the current push token and remove all listeners.
 * Call this on logout.
 */
export async function unregisterPush(): Promise<void> {
  if (_currentToken) {
    try {
      await api.delete(`/devices/${encodeURIComponent(_currentToken)}`)
    } catch {
      // Best-effort
    }
    _currentToken = null
  }

  for (const handle of _listeners) {
    try { await handle.remove() } catch { /* ignore */ }
  }
  _listeners = []
  _listenersAttached = false
}
